import {useMemo} from 'react';
import TabComponent from '../../common/components/TabComponent';
import {Navigate, useLocation} from 'react-router-dom';
import {ColumnDef} from '@tanstack/react-table';
import {useIntl} from 'react-intl';
import {getFiles, FileData} from './api';
import {useQuery} from 'react-query';
import {useLang} from '../../../_metronic/i18n/Metronici18n';
import {formatDate} from '../../common/tools/date';
import {formatBytes} from '../../common/tools/filesize';
import EmptyDocuments from './EmptyDocuments';
import DownloadIcon from '../../common/components/DownloadIcon';
import DocumentDownloadButton from '../../../Domains/Document/Components/DocumentDownloadButton';
import Loading from '../../common/components/Loading';
import TableMetronic from '../../common/components/table/TableMetronic';
import styles from './DocumentsManager.module.scss';
import clsx from 'clsx';

const DocumentsManager = () => {
    const intl = useIntl();
    const currentPage = useLocation().pathname.split('/').pop();
    const lang = useLang();

    const filesQuery = useQuery({queryKey: ['files', lang], queryFn: () => getFiles(lang)});

    const columns = useMemo<ColumnDef<FileData>[]>(
        () => [
            {
                header: intl.formatMessage({id: 'DOCUMENTS.TABLE.TYPE'}),
                accessorKey: 'type',
                size: 140,
            },
            {
                header: intl.formatMessage({id: 'DOCUMENTS.TABLE.FILE'}),
                accessorKey: 'fileName',
                size: 600,
                cell: (info) => info.getValue(),
            },
            {
                header: intl.formatMessage({id: 'DOCUMENTS.TABLE.DATE'}),
                accessorKey: 'dateUpload',
                size: 110,
                cell: (info) => formatDate(info.getValue() as string),
            },
            {
                header: intl.formatMessage({id: 'DOCUMENTS.TABLE.FILESIZE'}),
                accessorKey: 'fileSize',
                size: 110,
                cell: (info) => formatBytes(info.getValue() as number),
            },
            {
                header: 'Action',
                size: 90,
                cell: (info) => (
                    <DocumentDownloadButton
                        file={info.row.original}
                        className="cursor-pointer btn btn-link p-0 border-0"
                        ariaLabel={intl.formatMessage({id: 'ADMINISTRATIVE.BILL.DOWNLOAD'})}
                    >
                        <DownloadIcon
                            tooltip={<span>{intl.formatMessage({id: 'ADMINISTRATIVE.BILL.DOWNLOAD'})}</span>}
                        />
                    </DocumentDownloadButton>
                ),
                enableSorting: false,
            },
        ],
        [intl]
    );

    const renderMobileCard = (file: FileData): JSX.Element => (
        <div className={styles.mobileCardBody}>
            <div className={styles.mobileCardHeader}>
                <span className={styles.mobileCardFileName}>{file.fileName}</span>
            </div>
            <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardLabel}>
                    {intl.formatMessage({id: 'DOCUMENTS.TABLE.TYPE'})}
                </span>
                <span className={styles.mobileCardValue}>{file.type}</span>
            </div>
            <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardLabel}>
                    {intl.formatMessage({id: 'DOCUMENTS.TABLE.DATE'})}
                </span>
                <span>{formatDate(file.dateUpload)}</span>
            </div>
            <div className={styles.mobileCardRow}>
                <span className={styles.mobileCardLabel}>
                    {intl.formatMessage({id: 'DOCUMENTS.TABLE.FILESIZE'})}
                </span>
                <span>{formatBytes(file.fileSize)}</span>
            </div>
            <div className={styles.mobileCardActions} onClick={(e) => e.stopPropagation()}>
                <DocumentDownloadButton
                    file={file}
                    className={clsx('btn btn-sm btn-light-primary', styles.mobileActionButton)}
                >
                    <i className="fa-solid fa-download fs-4 me-2"></i>
                    {intl.formatMessage({id: 'ADMINISTRATIVE.BILL.DOWNLOAD'})}
                </DocumentDownloadButton>
            </div>
        </div>
    );

    if (filesQuery.isLoading || filesQuery.isIdle) {
        return <Loading />;
    }

    if (filesQuery.isError) {
        return <p>{intl.formatMessage({id: 'DOCUMENTS.ERROR.FETCH'})}</p>;
    }

    const tabData: {title: string; data: FileData[]; key: string; permissions?: string[]}[] = [
        {
            title: intl.formatMessage({id: 'DOCUMENTS.TAB.COMMERCIAL'}),
            data: filesQuery.data.commercial,
            key: 'commercial',
        },
        {
            title: intl.formatMessage({id: 'DOCUMENTS.TAB.TECHNIQUE'}),
            data: filesQuery.data.technique,
            key: 'technique',
            permissions: ['voirDocumentsTechniques'],
        },
        {
            title: intl.formatMessage({id: 'DOCUMENTS.TAB.ADMINISTRATIF'}),
            data: filesQuery.data.administratif,
            key: 'administratif',
        },
        {
            title: intl.formatMessage({id: 'DOCUMENTS.TAB.FIBRE'}),
            data: filesQuery.data.fibre,
            key: 'fibre',
        },
        {
            title: intl.formatMessage({id: 'DOCUMENTS.TAB.RAPPORT_ROC'}),
            data: filesQuery.data.rapportRoc,
            key: 'rapport-roc',
        },
        {
            title: intl.formatMessage({id: 'DOCUMENTS.TAB.SERVICE_CLIENT'}),
            data: filesQuery.data.serviceClient,
            key: 'service-client',
        },
    ].filter((tab) => {
        return tab.data && tab.data.length > 0;
    });

    if (tabData.length === 0) {
        return <EmptyDocuments />;
    }

    if (currentPage !== 'bills' && !tabData.find((tab) => tab.key === currentPage)) {
        return <Navigate to={`/documents/${tabData[0].key}`} />;
    }

    // @ts-ignore
    return (
        <TabComponent
            // @ts-ignore
            tabData={tabData
                .map((tab) => {
                    return {
                        title: tab.title,
                        key: tab.key,
                        to: `/documents/${tab.key}`,
                        content: (
                            <TableMetronic
                                title={intl.formatMessage({id: 'DOCUMENTS.TABLE.TITLE'})}
                                columns={columns}
                                renderMobileCard={renderMobileCard}
                                defaultSorting={[{id: 'dateUpload', desc: true}]}
                                noRowsMessage={intl.formatMessage({id: 'DOCUMENTS.TABLE.EMPTY'})}
                                data={tab.data}
                                pagination={true}
                                searchBar={true}
                            />
                        ),
                        permissions: tab.permissions ? tab.permissions : [],
                    };
                })}
            defaultTabKey={currentPage}
            activeKey={currentPage ?? ''}
        />
    );
};

export default DocumentsManager;
